/**
 * OutreachSendCard — the special activity card for Event Outreach send
 * tasks (type='task' + task_kind='outreach_send'). Renders in the
 * contact Activities feed and on the Tasks page. Provides:
 *
 *   - Message body (editable in place)
 *   - Copy to clipboard
 *   - Mark as sent (flips the task to completed + stamps sent_at)
 *   - Capture response (paste the reply the contact sent back)
 *   - Rework next message (regenerates the next message in the same
 *     channel using the full conversation chain)
 *
 * Rework routes the rep to the next task's card (same feed) so the
 * sequence feels continuous.
 */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { formatRelativeInZone } from '../../lib/timezone';
import { errorText } from '../../lib/apiErrors';
import { notifyTasksChanged } from '../../lib/tasksApi';
import { dueDayKey, dueState, dueText, dueTone, isTaskClosed } from '../../lib/taskDue';
import useDisplayZone from '../../hooks/useDisplayZone';
import {
    ChevronDown, ChevronUp, Copy, Check, Edit3, Save, X as XIcon,
    Loader2, Clock, Sparkles, Send, MessageCircle, Mail, Linkedin,
    Instagram, Facebook, Twitter,
} from 'lucide-react';
import TaskAssignment from './TaskAssignment';
import ActivityEditHistory from './ActivityEditHistory';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CHANNEL_VISUAL = {
    instagram_dm: { Icon: Instagram,     className: 'text-pink-500',    label: 'Instagram DM' },
    facebook_dm:  { Icon: Facebook,      className: 'text-blue-600',    label: 'Facebook DM' },
    linkedin:     { Icon: Linkedin,      className: 'text-sky-700',     label: 'LinkedIn' },
    whatsapp:     { Icon: MessageCircle, className: 'text-green-500',   label: 'WhatsApp' },
    x:            { Icon: Twitter,       className: 'text-stone-700',   label: 'X' },
    email:        { Icon: Mail,          className: 'text-indigo-500',  label: 'Email' },
};

// #532 residue — labelled, viewer-zone stamp (see ActivityCard.formatStamp).
function formatStamp(v, zone) { return formatRelativeInZone(v, { zone }); }

// The due line, by the rule every other task card follows (#904, D4): "Overdue · Oct 20" in red, "Due today"
// in amber, in the viewer's zone. A finished send task keeps its date, muted. A value that is not a calendar
// date has no state to colour and prints as stored.
function DueLine({ activity, zone }) {
    if (!dueDayKey(activity.due_date)) return <span>due {String(activity.due_date)}</span>;
    const state = isTaskClosed(activity) || activity.sent_at ? 'closed' : dueState(activity.due_date, zone);
    return <span className={dueTone(state)} data-testid="outreach-due" data-due-state={state}>{dueText(activity.due_date, state, zone)}</span>;
}

export default function OutreachSendCard({ activity, expanded, onToggle, onChanged, teamMembers = [] }) {
    const { user } = useAuth();
    const displayZone = useDisplayZone();
    const [editing, setEditing] = useState(false);
    const [body, setBody] = useState(activity?.description || '');
    const [subject, setSubject] = useState(activity?.message_subject || '');
    const [response, setResponse] = useState(activity?.response_text || '');
    const [saving, setSaving] = useState(false);
    const [marking, setMarking] = useState(false);
    const [capturing, setCapturing] = useState(false);
    const [reworking, setReworking] = useState(false);
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        setBody(activity?.description || '');
        setSubject(activity?.message_subject || '');
        setResponse(activity?.response_text || '');
        setEditing(false);
    }, [activity?.activity_id]);

    if (!activity) return null;
    const channel = activity.channel || 'email';
    const { Icon, className: tint, label } = CHANNEL_VISUAL[channel] || CHANNEL_VISUAL.email;
    const sessionId = activity.outreach_session_id;
    const messageId = activity.outreach_message_id;
    const isEmail = channel === 'email';
    const sent = Boolean(activity.sent_at) || activity.status === 'completed';
    const hasResponse = Boolean(activity.response_text);
    // If the activity has `sequence_index` === 2 it's the last — no next to rework.
    const canReworkNext = activity.sequence_index != null && activity.sequence_index < 2 && hasResponse;

    const isAuthor = user?.user_id && activity.owner_id === user.user_id;
    const isAdmin = user?.is_admin;
    const canEdit = activity.can_edit !== undefined ? Boolean(activity.can_edit) : Boolean(isAuthor || isAdmin);

    const token = () => localStorage.getItem('crm_token');
    const headers = () => {
        const t = token();
        return t ? { Authorization: `Bearer ${t}` } : {};
    };

    // --- Actions ---

    const save = async () => {
        if (!sessionId || !messageId) { toast.error('Missing outreach ids'); return; }
        setSaving(true);
        try {
            await axios.patch(
                `${API}/event-outreach/${sessionId}/messages/${messageId}`,
                { body, subject: isEmail ? subject : null },
                { headers: headers() },
            );
            toast.success('Saved');
            setEditing(false);
            // Mirror to the activity doc for immediate UI update.
            onChanged?.({ ...activity, description: body, message_subject: isEmail ? subject : null });
        } catch (err) {
            toast.error(errorText(err, 'Failed to save'));
        } finally { setSaving(false); }
    };

    const copy = () => {
        const prefix = isEmail && subject ? `Subject: ${subject}\n\n` : '';
        navigator.clipboard?.writeText(prefix + (body || activity.description || ''));
        setCopied(true);
        setTimeout(() => setCopied(false), 1200);
    };

    const markSent = async () => {
        if (!sessionId || !messageId) return;
        setMarking(true);
        try {
            await axios.post(
                `${API}/event-outreach/${sessionId}/messages/${messageId}/mark-sent`,
                null, { headers: headers() },
            );
            toast.success('Marked as sent');
            notifyTasksChanged();
            onChanged?.({ ...activity, status: 'completed', sent_at: new Date().toISOString() });
        } catch (err) {
            toast.error(errorText(err, 'Failed to mark sent'));
        } finally { setMarking(false); }
    };

    const captureResponse = async () => {
        if (!response.trim()) { toast.error('Paste the reply first'); return; }
        setCapturing(true);
        try {
            await axios.post(
                `${API}/event-outreach/${sessionId}/messages/${messageId}/capture-response`,
                { response_text: response.trim() },
                { headers: headers() },
            );
            toast.success('Response captured');
            onChanged?.({ ...activity, response_text: response.trim(), response_captured_at: new Date().toISOString() });
        } catch (err) {
            toast.error(errorText(err, 'Failed to capture response'));
        } finally { setCapturing(false); }
    };

    const reworkNext = async () => {
        setReworking(true);
        try {
            const r = await axios.post(
                `${API}/event-outreach/${sessionId}/messages/${messageId}/rework-next`,
                null, { headers: headers() },
            );
            toast.success('Next message reworked with the response');
            // Nudge parent to refresh its activity list so the reworked
            // task's body updates in-feed.
            onChanged?.(activity, { reworked: true, reworked_message_id: r.data?.reworked_message_id });
        } catch (err) {
            toast.error(errorText(err, 'Failed to rework next message'));
        } finally { setReworking(false); }
    };

    // --- Render ---

    const bodyText = body || activity.description || '';

    return (
        <div className="border border-violet-200 rounded-lg bg-white overflow-hidden" data-activity-id={activity.activity_id}>
            {/* Header */}
            <div className="flex items-start gap-3 p-3 bg-violet-50/30 border-b border-violet-100">
                <button
                    onClick={onToggle}
                    className="flex-shrink-0 p-0.5 text-violet-500 hover:text-violet-800"
                    aria-label={expanded ? 'Collapse' : 'Expand'}
                >
                    {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </button>
                <div className="w-7 h-7 rounded-full bg-white flex items-center justify-center flex-shrink-0 ring-1 ring-violet-200">
                    <Icon className={`w-3.5 h-3.5 ${tint}`} />
                </div>
                <button onClick={onToggle} className="flex-1 min-w-0 text-left">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-semibold uppercase tracking-wider bg-violet-600 text-white rounded-full px-1.5 py-0.5 flex items-center gap-1">
                            <Sparkles className="w-2.5 h-2.5" /> AI
                        </span>
                        <span className="text-sm font-semibold text-stone-800">{label}</span>
                        {activity.title && (
                            <span className="text-xs text-stone-500 truncate">· {activity.title.split(' — ').slice(1).join(' — ') || activity.title}</span>
                        )}
                        {sent && <span className="text-[10px] text-emerald-600 font-medium ml-auto">Sent ✓</span>}
                    </div>
                    <div className="flex items-center gap-1.5 mt-0.5 text-[11px] text-stone-500">
                        <Clock className="w-3 h-3" />
                        {activity.due_date
                            ? <DueLine activity={activity} zone={displayZone} />
                            : <span>{formatStamp(activity.created_at, displayZone)}</span>}
                        {activity.sequence_index != null && <span>· step {activity.sequence_index + 1} of 3</span>}
                    </div>
                </button>
            </div>

            {/* Collapsed body preview */}
            <TaskAssignment activity={activity} teamMembers={teamMembers} onSaved={onChanged} />
            <ActivityEditHistory activity={activity} displayZone={displayZone} />


            {!expanded && bodyText && (
                <button onClick={onToggle} className="block w-full text-left px-3 py-2 text-xs text-stone-500 line-clamp-2 whitespace-pre-wrap hover:text-stone-700">
                    {bodyText}
                </button>
            )}

            {/* Expanded */}
            {expanded && (
                <div className="p-3 space-y-3">
                    {editing ? (
                        <div className="space-y-2">
                            {isEmail && (
                                <input
                                    value={subject}
                                    onChange={(e) => setSubject(e.target.value)}
                                    placeholder="Subject"
                                    className="w-full px-2 py-1.5 text-sm border border-stone-200 rounded focus:outline-none focus:ring-2 focus:ring-violet-300"
                                />
                            )}
                            <textarea
                                value={body}
                                onChange={(e) => setBody(e.target.value)}
                                rows={7}
                                className="w-full px-2 py-1.5 text-sm border border-stone-200 rounded focus:outline-none focus:ring-2 focus:ring-violet-300 resize-vertical"
                            />
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={save}
                                    disabled={saving}
                                    className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-stone-900 text-white rounded hover:bg-stone-800 disabled:opacity-60"
                                >
                                    {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Save className="w-3 h-3" />}
                                    Save
                                </button>
                                <button onClick={() => { setEditing(false); setBody(activity.description || ''); setSubject(activity.message_subject || ''); }} className="text-xs text-stone-500 hover:text-stone-800">Cancel</button>
                            </div>
                        </div>
                    ) : (
                        <>
                            {isEmail && activity.message_subject && (
                                <div className="text-xs text-stone-600">
                                    <span className="font-semibold">Subject:</span> {activity.message_subject}
                                </div>
                            )}
                            <p className="text-sm text-stone-700 whitespace-pre-wrap leading-relaxed">{bodyText || <em className="text-stone-400">(empty)</em>}</p>

                            <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-stone-100">
                                <button onClick={copy} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-white border border-stone-200 rounded hover:bg-stone-50 text-stone-700">
                                    <Copy className="w-3 h-3" /> {copied ? 'Copied' : 'Copy message'}
                                </button>
                                {canEdit && !sent && (
                                    <button onClick={() => setEditing(true)} className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-white border border-stone-200 rounded hover:bg-stone-50 text-stone-700">
                                        <Edit3 className="w-3 h-3" /> Edit
                                    </button>
                                )}
                                {!sent && (
                                    <button
                                        onClick={markSent}
                                        disabled={marking}
                                        className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-violet-600 text-white rounded hover:bg-violet-700 disabled:opacity-60 ml-auto"
                                    >
                                        {marking ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
                                        Mark as sent
                                    </button>
                                )}
                            </div>

                            {/* Response capture — only meaningful after sending, and
                                only on messages 1 & 2 (message 3 has no "next"). */}
                            {sent && activity.sequence_index != null && activity.sequence_index < 2 && (
                                <div className="pt-2 border-t border-stone-100 space-y-2">
                                    <div className="text-xs font-semibold text-stone-500 uppercase tracking-wider">
                                        Response received
                                    </div>
                                    <textarea
                                        value={response}
                                        onChange={(e) => setResponse(e.target.value)}
                                        rows={3}
                                        placeholder="Paste what the contact replied — AI will use it to tune the next message…"
                                        className="w-full px-2 py-1.5 text-sm border border-stone-200 rounded focus:outline-none focus:ring-2 focus:ring-violet-300 resize-vertical"
                                    />
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <button
                                            onClick={captureResponse}
                                            disabled={capturing || response.trim() === (activity.response_text || '').trim()}
                                            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-white border border-stone-200 rounded hover:bg-stone-50 text-stone-700 disabled:opacity-60"
                                        >
                                            {capturing ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                                            Capture response
                                        </button>
                                        <button
                                            onClick={reworkNext}
                                            disabled={reworking || !canReworkNext}
                                            title={!canReworkNext ? 'Capture the response first' : 'Regenerate the next message with this context'}
                                            className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium bg-violet-600 text-white rounded hover:bg-violet-700 disabled:opacity-50"
                                        >
                                            {reworking ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                                            Rework next message
                                        </button>
                                    </div>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
}
